import { SymbolPicker } from '../style/SymbolPicker';
import { BackingControls } from '../style/BackingControls';
import { markerPresentation } from '../../../symbols/scene';
import { SymbolColourControl } from '../style/SymbolColourControl';
import { isImageGraphic } from '../../../symbols/model';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { rectangleRing } from '../../../annotations/defaults';
import { annotationHeight } from '../../../annotations/elevation';
import { normalizeRotationDegrees } from '../../../annotations/geometry';
import {
  annotationLayerLegendEligibility,
  annotationLayerLegendEligibilityMessage,
  annotationLegendSymbol,
} from '../../../annotations/legend';
import {
  ANNOTATION_LIMITS,
  type Annotation,
  type AnnotationCreationTool,
  type AnnotationEndpointStyle,
} from '../../../annotations/model';
import { Icon, type IconName } from '../../../components/Icon';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { ContextHelp } from '../../../components/ContextHelp';
import { FormattingButton } from '../../../components/FormattingButton';
import { supportsAuthoredItalic } from '../../../typography/fonts';
import { NumericControl } from '../style/NumericControl';
import { useCapability } from '../../../features/useCapability';
import {
  ANNOTATION_FONT_FAMILIES,
  ANNOTATION_FONT_WEIGHTS,
  ANNOTATION_HALOS,
  ANNOTATION_PRESETS,
  applyAnnotationPreset,
  matchingAnnotationPreset,
  type AnnotationFontFamilyId,
} from '../../../annotations/typography';
import { estimateTextBoxLayout } from '../../../annotations/textLayout';
import { useAppStore } from '../../state/useAppStore';
import { AnchoredMenu } from '../AnchoredMenu';
import { ColourControl } from '../style/ColourControl';

const TOOLS: readonly {
  readonly id: AnnotationCreationTool;
  readonly label: string;
  readonly icon: IconName;
}[] = [
  { id: 'marker', label: 'Marker', icon: 'annotation-marker' },
  { id: 'line', label: 'Line / Arrow', icon: 'annotation-arrow' },
  { id: 'polygon', label: 'Polygon', icon: 'annotation-polygon' },
  { id: 'rectangle', label: 'Rectangle', icon: 'annotation-rectangle' },
  { id: 'ellipse', label: 'Ellipse / Circle', icon: 'annotation-ellipse' },
  { id: 'text', label: 'Text', icon: 'annotation-text' },
  { id: 'callout', label: 'Callout', icon: 'annotation-callout' },
  { id: 'focus-area', label: 'Focus area', icon: 'annotation-focus' },
];

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  disabled = false,
  slider = false,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step?: number | 'any';
  readonly disabled?: boolean;
  readonly slider?: boolean;
  readonly onChange: (value: number) => void;
}) {
  const id = useId();
  if (slider)
    return (
      <NumericControl
        id={id}
        label={label}
        min={min}
        max={max}
        step={typeof step === 'number' ? step : 1}
        value={value}
        onChange={onChange}
      />
    );
  return (
    <label className="annotation-field">
      <span>{label}</span>
      <input
        disabled={disabled}
        max={max}
        min={min}
        onChange={(event) => {
          if (Number.isFinite(event.target.valueAsNumber)) onChange(event.target.valueAsNumber);
        }}
        step={step}
        type="number"
        value={value}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  children,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly children: ReactNode;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="annotation-field">
      <span>{label}</span>
      <select aria-label={label} onChange={(event) => onChange(event.target.value)} value={value}>
        {children}
      </select>
    </label>
  );
}

function TextControls({
  annotation,
  change,
}: {
  readonly annotation: Extract<Annotation, { type: 'text' | 'callout' }>;
  readonly change: (update: (annotation: Annotation) => Annotation) => void;
}) {
  const style = annotation.style;
  const patchStyle = (patch: Partial<typeof style>) =>
    change((item) =>
      item.type === annotation.type
        ? ({ ...item, style: { ...item.style, ...patch } } as Annotation)
        : item,
    );
  return (
    <>
      <div className="annotation-style-presets" role="group" aria-label="Annotation style">
        {ANNOTATION_PRESETS.map(({ id, label }) => (
          <button
            type="button"
            key={id}
            aria-label={label}
            aria-pressed={matchingAnnotationPreset(annotation) === id}
            onClick={() =>
              change((item) =>
                item.type === 'text' || item.type === 'callout'
                  ? applyAnnotationPreset(item, id)
                  : item,
              )
            }
          >
            <span
              aria-hidden="true"
              className={`annotation-preset-preview annotation-preset-${id}`}
            >
              Aa
            </span>
            <span>{label}</span>
            {matchingAnnotationPreset(annotation) === id ? (
              <span aria-hidden="true" className="annotation-preset-check">
                ✓
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {!matchingAnnotationPreset(annotation) ? (
        <small className="annotation-style-status">Custom</small>
      ) : null}
      <label className="annotation-field">
        <span>Text</span>
        <textarea
          maxLength={4000}
          onChange={(event) =>
            change((item) =>
              item.type === annotation.type
                ? ({ ...item, text: event.target.value || ' ' } as Annotation)
                : item,
            )
          }
          rows={3}
          value={annotation.text}
        />
      </label>
      <fieldset className="annotation-appearance-group">
        <legend>Typography</legend>
        <div className="annotation-field-pair annotation-font-family-row">
          <SelectField
            label="Font family"
            onChange={(fontFamily) =>
              patchStyle({
                fontFamily: fontFamily as AnnotationFontFamilyId,
                ...(!supportsAuthoredItalic(fontFamily as AnnotationFontFamilyId)
                  ? { fontStyle: 'normal' }
                  : {}),
              })
            }
            value={style.fontFamily}
          >
            {ANNOTATION_FONT_FAMILIES.map((font) => (
              <option key={font.id} value={font.id}>
                {font.label}
              </option>
            ))}
          </SelectField>
          <div className="annotation-formatting-buttons labels-font-style">
            <FormattingButton
              label="Italic"
              pressed={style.fontStyle === 'italic'}
              disabled={!supportsAuthoredItalic(style.fontFamily)}
              onClick={() =>
                patchStyle({ fontStyle: style.fontStyle === 'italic' ? 'normal' : 'italic' })
              }
            />
          </div>
        </div>
        {!supportsAuthoredItalic(style.fontFamily) ? (
          <small className="annotation-style-status">Italic is unavailable for Manrope.</small>
        ) : null}
        <div className="annotation-field-pair">
          <SelectField
            label="Font weight"
            value={String(style.fontWeight ?? 400)}
            onChange={(value) => patchStyle({ fontWeight: Number(value) as 400 | 500 | 600 | 700 })}
          >
            {ANNOTATION_FONT_WEIGHTS.map((weight, index) => (
              <option value={weight} key={weight}>
                {['Regular', 'Medium', 'Semibold', 'Bold'][index]}
              </option>
            ))}
          </SelectField>
          <NumberField
            label="Size (px)"
            max={96}
            min={8}
            onChange={(fontSize) => patchStyle({ fontSize })}
            value={style.fontSize}
          />
        </div>
        <div className="colour-field-pair">
          <ColourControl
            id={`annotation-${annotation.id}-text`}
            label="Text colour"
            onChange={(colour) => patchStyle({ colour })}
            value={style.colour}
          />
          {(style.textHaloWidth ?? 0) > 0 ? (
            <ColourControl
              id={`annotation-${annotation.id}-halo`}
              label="Halo colour"
              value={style.textHaloColour ?? '#ffffff'}
              onChange={(textHaloColour) => patchStyle({ textHaloColour })}
            />
          ) : null}
        </div>
        <div className="annotation-field-pair">
          <SelectField
            label="Alignment"
            onChange={(alignment) => patchStyle({ alignment: alignment as typeof style.alignment })}
            value={style.alignment}
          >
            <option value="left">Left</option>
            <option value="centre">Centre</option>
            <option value="right">Right</option>
          </SelectField>
          <SelectField
            label="Text halo"
            value={String(style.textHaloWidth ?? 0)}
            onChange={(value) => patchStyle({ textHaloWidth: Number(value) })}
          >
            {ANNOTATION_HALOS.map(({ label, width }) => (
              <option value={width} key={label}>
                {label}
              </option>
            ))}
            {!ANNOTATION_HALOS.some(({ width }) => width === (style.textHaloWidth ?? 0)) ? (
              <option value={style.textHaloWidth}>Custom ({style.textHaloWidth} px)</option>
            ) : null}
          </SelectField>
        </div>
      </fieldset>
      <fieldset className="annotation-appearance-group">
        <legend>Box</legend>
        <div className="colour-field-pair">
          <ColourControl
            id={`annotation-${annotation.id}-background`}
            label="Background"
            onChange={(backgroundColour) => patchStyle({ backgroundColour })}
            value={style.backgroundColour}
          />
          <ColourControl
            id={`annotation-${annotation.id}-border`}
            label="Border colour"
            onChange={(borderColour) => patchStyle({ borderColour })}
            value={style.borderColour}
          />
        </div>
        <div className="annotation-field-pair">
          <NumberField
            label="Background opacity (%)"
            max={100}
            min={0}
            onChange={(value) => patchStyle({ backgroundOpacity: value / 100 })}
            value={Math.round(style.backgroundOpacity * 100)}
          />
          <NumberField
            label="Border width"
            max={32}
            min={0}
            onChange={(borderWidth) => patchStyle({ borderWidth })}
            step={0.25}
            value={style.borderWidth}
          />
        </div>
        <div className="annotation-field-pair">
          <NumberField
            label="Text box width (px)"
            max={800}
            min={40}
            onChange={(boxWidth) => patchStyle({ boxWidth })}
            value={style.boxWidth}
          />
          <NumberField
            label="Text box height (px)"
            min={8}
            max={1200}
            value={style.boxHeight ?? estimateTextBoxLayout(annotation.text, style).contentHeight}
            disabled={style.boxHeight === undefined}
            onChange={(boxHeight) => patchStyle({ boxHeight })}
          />
        </div>
        <label className="annotation-checkbox">
          <input
            type="checkbox"
            checked={style.boxHeight === undefined}
            onChange={(event) => {
              if (!event.target.checked)
                patchStyle({
                  boxHeight: estimateTextBoxLayout(annotation.text, style).contentHeight,
                });
              else
                change((item) => {
                  if (item.type !== 'text' && item.type !== 'callout') return item;
                  const { boxHeight, ...rest } = item.style;
                  void boxHeight;
                  return { ...item, style: rest } as Annotation;
                });
            }}
          />
          Automatic height
        </label>
        {style.boxHeight !== undefined &&
        style.boxHeight < estimateTextBoxLayout(annotation.text, style).textHeight ? (
          <small className="annotation-style-status">The box expands to fit its text.</small>
        ) : null}
        <div className="annotation-field-pair">
          <NumberField
            label="Padding"
            max={32}
            min={0}
            onChange={(padding) => patchStyle({ padding })}
            value={style.padding}
          />
          <NumberField
            label="Corner radius (px)"
            max={ANNOTATION_LIMITS.cornerRadius.maximum}
            min={ANNOTATION_LIMITS.cornerRadius.minimum}
            onChange={(cornerRadius) =>
              patchStyle({
                cornerRadius: Math.max(
                  ANNOTATION_LIMITS.cornerRadius.minimum,
                  Math.min(ANNOTATION_LIMITS.cornerRadius.maximum, cornerRadius),
                ),
              })
            }
            value={style.cornerRadius}
          />
        </div>
      </fieldset>
    </>
  );
}

function ObjectControls({
  annotation,
  change,
}: {
  readonly annotation: Annotation;
  readonly change: (update: (annotation: Annotation) => Annotation) => void;
}) {
  const activeLayerId = useAppStore((s) => s.shell.activeAnnotationLayerId);
  const styleChange = (patch: Record<string, unknown>) =>
    change((item) => ({ ...item, style: { ...item.style, ...patch } }) as Annotation);
  if (annotation.type === 'marker')
    return (
      <>
        <SymbolPicker
          target={{ kind: 'marker', layerId: activeLayerId ?? '', annotationId: annotation.id }}
          shapes={['circle', 'square', 'diamond']}
          graphic={annotation.style.shape}
          presentation={markerPresentation(annotation.style)}
          colour={annotation.style.fillColour}
          {...(annotation.style.tint === undefined ? {} : { tint: annotation.style.tint })}
          onChange={(shape, anchor) => styleChange({ shape, anchor })}
        />
        <div className={isImageGraphic(annotation.style.shape) ? undefined : 'colour-field-pair'}>
          <SymbolColourControl
            id={`annotation-${annotation.id}-fill`}
            label="Fill colour"
            graphic={annotation.style.shape}
            colour={annotation.style.fillColour}
            {...(annotation.style.tint === undefined ? {} : { tint: annotation.style.tint })}
            onColour={(fillColour) => styleChange({ fillColour })}
            onTint={(tint) => styleChange({ tint })}
          />
          {!isImageGraphic(annotation.style.shape) ? (
            <ColourControl
              id={`annotation-${annotation.id}-outline`}
              label="Outline colour"
              onChange={(outlineColour) => styleChange({ outlineColour })}
              value={annotation.style.outlineColour}
            />
          ) : null}
        </div>
        {isImageGraphic(annotation.style.shape) ? (
          <>
            <NumberField
              label="Rotation (°)"
              min={-180}
              max={180}
              value={annotation.style.rotationDegrees ?? 0}
              onChange={(rotationDegrees) => styleChange({ rotationDegrees })}
            />
            <div className="style-field style-field-inline">
              <label htmlFor={`annotation-${annotation.id}-anchor`}>Anchor</label>
              <select
                id={`annotation-${annotation.id}-anchor`}
                value={annotation.style.anchor ?? 'center'}
                onChange={(e) => styleChange({ anchor: e.target.value })}
              >
                <option value="center">Centre</option>
                <option value="bottom">Bottom centre</option>
              </select>
            </div>
          </>
        ) : null}
        <div className="annotation-field-pair">
          <NumberField
            label="Size"
            max={64}
            min={4}
            onChange={(size) => styleChange({ size })}
            value={annotation.style.size}
          />
          <NumberField
            label="Opacity (%)"
            max={100}
            min={0}
            onChange={(value) => styleChange({ opacity: value / 100 })}
            value={Math.round(annotation.style.opacity * 100)}
          />
        </div>
        <BackingControls
          graphic={annotation.style.shape}
          colour={annotation.style.fillColour}
          tint={annotation.style.tint}
          size={annotation.style.size}
          backing={annotation.style.backing}
          onChange={(backing) => styleChange({ backing })}
        />
      </>
    );
  if (annotation.type === 'line')
    return (
      <>
        <ColourControl
          id={`annotation-${annotation.id}-colour`}
          label="Colour"
          onChange={(colour) => styleChange({ colour })}
          value={annotation.style.colour}
        />
        <NumberField
          label="Width"
          max={32}
          min={0.25}
          onChange={(width) => styleChange({ width })}
          step={0.25}
          value={annotation.style.width}
        />
        <NumberField
          slider
          label="Opacity (%)"
          max={100}
          min={0}
          onChange={(value) => styleChange({ opacity: value / 100 })}
          value={Math.round(annotation.style.opacity * 100)}
        />
        <SelectField
          label="Stroke"
          onChange={(dash) => styleChange({ dash })}
          value={annotation.style.dash}
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
        </SelectField>
        <SelectField
          label="Endpoint at end"
          onChange={(endStyle) => styleChange({ endStyle: endStyle as AnnotationEndpointStyle })}
          value={annotation.style.endStyle}
        >
          <option value="none">None</option>
          <option value="arrow">Arrowhead</option>
          <option value="filled-circle">Filled circle</option>
          <option value="filled-square">Filled square</option>
        </SelectField>
      </>
    );
  if (
    annotation.type === 'polygon' ||
    annotation.type === 'rectangle' ||
    annotation.type === 'ellipse'
  )
    return (
      <>
        {annotation.type === 'rectangle' || annotation.type === 'ellipse' ? (
          <NumberField
            label="Rotation (°)"
            max={359.999}
            min={0}
            onChange={(rotationDegrees) =>
              change((item) =>
                item.type === 'rectangle' || item.type === 'ellipse'
                  ? { ...item, rotationDegrees: normalizeRotationDegrees(rotationDegrees) }
                  : item,
              )
            }
            step={1}
            value={annotation.rotationDegrees}
          />
        ) : null}
        {annotation.type === 'rectangle' ? (
          <NumberField
            label="Corner radius (px)"
            max={ANNOTATION_LIMITS.cornerRadius.maximum}
            min={ANNOTATION_LIMITS.cornerRadius.minimum}
            onChange={(cornerRadius) =>
              styleChange({
                cornerRadius: Math.max(
                  ANNOTATION_LIMITS.cornerRadius.minimum,
                  Math.min(ANNOTATION_LIMITS.cornerRadius.maximum, cornerRadius),
                ),
              })
            }
            value={annotation.style.cornerRadius}
          />
        ) : null}
        {annotation.type === 'ellipse' ? (
          <label className="annotation-check">
            <input
              checked={annotation.circleLocked}
              onChange={(event) =>
                change((item) =>
                  item.type === 'ellipse' ? { ...item, circleLocked: event.target.checked } : item,
                )
              }
              type="checkbox"
            />
            <span>Circle / lock proportions</span>
          </label>
        ) : null}
        <div className="colour-field-pair">
          <ColourControl
            id={`annotation-${annotation.id}-fill`}
            label="Fill colour"
            onChange={(fillColour) => styleChange({ fillColour })}
            value={annotation.style.fillColour}
          />
          <ColourControl
            id={`annotation-${annotation.id}-outline`}
            label="Outline colour"
            onChange={(outlineColour) => styleChange({ outlineColour })}
            value={annotation.style.outlineColour}
          />
        </div>
        <NumberField
          slider
          label="Fill opacity (%)"
          max={100}
          min={0}
          onChange={(value) => styleChange({ fillOpacity: value / 100 })}
          value={Math.round(annotation.style.fillOpacity * 100)}
        />
        <NumberField
          label="Outline width"
          max={32}
          min={0.25}
          onChange={(outlineWidth) => styleChange({ outlineWidth })}
          step={0.25}
          value={annotation.style.outlineWidth}
        />
        <SelectField
          label="Outline"
          onChange={(outlineDash) => styleChange({ outlineDash })}
          value={annotation.style.outlineDash}
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
        </SelectField>
      </>
    );
  if (annotation.type === 'text' || annotation.type === 'callout')
    return (
      <>
        <TextControls annotation={annotation} change={change} />
        {annotation.type === 'callout' ? (
          <fieldset className="annotation-appearance-group">
            <legend>Leader</legend>
            <div className="annotation-field-pair">
              <SelectField
                label="Leader style"
                value={annotation.style.leaderStyle ?? 'straight'}
                onChange={(leaderStyle) =>
                  styleChange({ leaderStyle: leaderStyle as 'tapered' | 'straight' })
                }
              >
                <option value="tapered">Tapered</option>
                <option value="straight">Straight</option>
              </SelectField>
              <SelectField
                label="Line style"
                onChange={(leaderDash) => styleChange({ leaderDash })}
                value={annotation.style.leaderDash}
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
              </SelectField>
            </div>
            <label className="annotation-checkbox">
              <input
                type="checkbox"
                checked={annotation.keepLeaderVerticalOnScreen ?? false}
                onChange={(event) =>
                  change((item) =>
                    item.type === 'callout'
                      ? { ...item, keepLeaderVerticalOnScreen: event.target.checked }
                      : item,
                  )
                }
              />
              <span>Keep leader vertical on screen</span>
            </label>

            <ColourControl
              id={`annotation-${annotation.id}-leader`}
              label="Leader colour"
              onChange={(leaderColour) => styleChange({ leaderColour })}
              value={annotation.style.leaderColour}
            />
            <div className="annotation-field-pair">
              <NumberField
                label="Leader width"
                max={32}
                min={0.25}
                onChange={(leaderWidth) => styleChange({ leaderWidth })}
                step={0.25}
                value={annotation.style.leaderWidth}
              />
              <NumberField
                label="Leader opacity (%)"
                min={0}
                max={100}
                step={5}
                value={(annotation.style.leaderOpacity ?? 1) * 100}
                onChange={(percent) => styleChange({ leaderOpacity: percent / 100 })}
              />
            </div>
            <SelectField
              label="Marker at target"
              onChange={(targetStyle) =>
                styleChange({ targetStyle: targetStyle as AnnotationEndpointStyle })
              }
              value={annotation.style.targetStyle}
            >
              <option value="none">None</option>
              <option value="arrow">Arrowhead</option>
              <option value="filled-circle">Filled circle</option>
              <option value="filled-square">Filled square</option>
            </SelectField>
          </fieldset>
        ) : null}
      </>
    );
  return (
    <>
      <ColourControl
        id={`annotation-${annotation.id}-fade`}
        label="Fade colour"
        onChange={(fadeColour) => styleChange({ fadeColour })}
        value={annotation.style.fadeColour}
      />
      <NumberField
        slider
        label="Fade opacity (%)"
        max={90}
        min={0}
        onChange={(value) => styleChange({ fadeOpacity: value / 100 })}
        value={Math.round(annotation.style.fadeOpacity * 100)}
      />
      <SelectField
        label="Fade"
        onChange={(fadeDirection) => styleChange({ fadeDirection })}
        value={annotation.style.fadeDirection}
      >
        <option value="outside">Outside focus area</option>
        <option value="inside">Inside focus area</option>
      </SelectField>
      <SelectField
        label="Apply fade to"
        onChange={(scope) => styleChange({ scope })}
        value={annotation.style.scope}
      >
        <option value="basemap">Basemap only</option>
        <option value="basemap-and-data">Basemap + data layers</option>
      </SelectField>
      <label className="annotation-check">
        <input
          checked={annotation.style.boundaryVisible}
          onChange={(event) => styleChange({ boundaryVisible: event.target.checked })}
          type="checkbox"
        />
        <span>Show boundary</span>
      </label>
      <ColourControl
        id={`annotation-${annotation.id}-boundary`}
        label="Boundary colour"
        onChange={(boundaryColour) => styleChange({ boundaryColour })}
        value={annotation.style.boundaryColour}
      />
      <NumberField
        label="Boundary width"
        max={32}
        min={0.25}
        onChange={(boundaryWidth) => styleChange({ boundaryWidth })}
        step={0.25}
        value={annotation.style.boundaryWidth}
      />
      <SelectField
        label="Boundary"
        onChange={(boundaryDash) => styleChange({ boundaryDash })}
        value={annotation.style.boundaryDash}
      >
        <option value="solid">Solid</option>
        <option value="dashed">Dashed</option>
      </SelectField>
      <small className="reserved-note">
        One Focus effect may be active project-wide. Other Focus Areas remain stored as alternate
        presentation configurations.
      </small>
    </>
  );
}

function GeometryControls({
  annotation,
  change,
}: {
  readonly annotation: Annotation;
  readonly change: (update: (annotation: Annotation) => Annotation) => void;
}) {
  const coordinateFields = (
    label: string,
    coordinate: readonly [number, number],
    update: (coordinate: readonly [number, number]) => void,
  ) => (
    <div className="annotation-coordinate-row" key={label}>
      <span>{label}</span>
      <input
        aria-label={`${label} longitude`}
        max={180}
        min={-180}
        onChange={(event) =>
          Number.isFinite(event.target.valueAsNumber) &&
          update([event.target.valueAsNumber, coordinate[1]])
        }
        step="0.000001"
        type="number"
        value={coordinate[0]}
      />
      <input
        aria-label={`${label} latitude`}
        max={85.051129}
        min={-85.051129}
        onChange={(event) =>
          Number.isFinite(event.target.valueAsNumber) &&
          update([coordinate[0], event.target.valueAsNumber])
        }
        step="0.000001"
        type="number"
        value={coordinate[1]}
      />
    </div>
  );
  if (annotation.type === 'marker' || annotation.type === 'text')
    return coordinateFields('Position', annotation.position, (position) =>
      change((item) =>
        item.type === annotation.type ? ({ ...item, position } as Annotation) : item,
      ),
    );
  if (annotation.type === 'callout')
    return (
      <>
        {coordinateFields('Target', annotation.target, (target) =>
          change((item) => (item.type === 'callout' ? { ...item, target } : item)),
        )}
        {coordinateFields('Text position', annotation.textPosition, (textPosition) =>
          change((item) => (item.type === 'callout' ? { ...item, textPosition } : item)),
        )}
      </>
    );
  if (annotation.type === 'rectangle')
    return (
      <>
        {coordinateFields('Corner 1', annotation.coordinates[0]!, (corner) =>
          change((item) =>
            item.type === 'rectangle'
              ? { ...item, coordinates: rectangleRing(corner, item.coordinates[2]!) }
              : item,
          ),
        )}
        {coordinateFields('Corner 2', annotation.coordinates[2]!, (corner) =>
          change((item) =>
            item.type === 'rectangle'
              ? { ...item, coordinates: rectangleRing(item.coordinates[0]!, corner) }
              : item,
          ),
        )}
      </>
    );
  if (annotation.type === 'ellipse')
    return (
      <>
        {coordinateFields('Bound 1', annotation.bounds[0], (bound) =>
          change((item) =>
            item.type === 'ellipse' ? { ...item, bounds: [bound, item.bounds[1]] } : item,
          ),
        )}
        {coordinateFields('Bound 2', annotation.bounds[1], (bound) =>
          change((item) =>
            item.type === 'ellipse' ? { ...item, bounds: [item.bounds[0], bound] } : item,
          ),
        )}
      </>
    );
  if (
    (annotation.type === 'polygon' || annotation.type === 'focus-area') &&
    annotation.additionalParts?.length
  )
    return (
      <p className="reserved-note">
        This area contains multiple parts. You can move it as one object, but vertex editing is
        unavailable for multipart areas.
      </p>
    );
  const editable = annotation.coordinates.slice(0, annotation.type === 'line' ? undefined : -1);
  return (
    <div className="annotation-vertices">
      {(annotation.type === 'polygon' || annotation.type === 'focus-area') &&
      annotation.holes?.length ? (
        <p>
          Interior holes are preserved. Vertex controls edit the exterior boundary; moving the
          object moves every ring.
        </p>
      ) : null}
      {editable.map((coordinate, index) =>
        coordinateFields(`Vertex ${index + 1}`, coordinate, (next) =>
          change((item) => {
            if (item.type !== annotation.type) return item;
            const coordinates = [...item.coordinates];
            coordinates[index] = next;
            if (item.type !== 'line' && index === 0) coordinates[coordinates.length - 1] = next;
            return { ...item, coordinates } as Annotation;
          }),
        ),
      )}
    </div>
  );
}

export function AnnotationInspector() {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const layerMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const canAuthor = useCapability('annotations.author');
  const project = useAppStore((state) => state.project);
  const shell = useAppStore((state) => state.shell);
  const startDrawing = useAppStore((state) => state.startAnnotationDrawing);
  const cancelDrawing = useAppStore((state) => state.cancelAnnotationDrawing);
  const selectLayer = useAppStore((state) => state.selectAnnotationLayer);
  const updateAnnotation = useAppStore((state) => state.updateAnnotation);
  const moveAnnotationToLayer = useAppStore((state) => state.moveAnnotationToLayer);
  const moveAnnotationToNewLayer = useAppStore((state) => state.moveAnnotationToNewLayer);
  const setAnnotationLayerLegend = useAppStore((state) => state.setAnnotationLayerLegend);
  const removeAnnotation = useAppStore((state) => state.removeAnnotation);
  const setActiveFocusArea = useAppStore((state) => state.setActiveFocusArea);
  const toggleLayerVisibility = useAppStore((state) => state.toggleAnnotationLayerVisibility);
  const closeInspector = useAppStore((state) => state.closeInspector);
  const layer = project.annotationLayers.find(
    (candidate) => candidate.id === shell.activeAnnotationLayerId,
  );
  const annotation = layer?.annotations.find(
    (candidate) => candidate.id === shell.selectedAnnotationId,
  );
  const [focusDialog, setFocusDialog] = useState<
    | { readonly kind: 'replace'; readonly existingId: string }
    | { readonly kind: 'switch'; readonly nextId: string; readonly currentName: string }
    | null
  >(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const removeSelectedAnnotation = useCallback(() => {
    if (!layer || !annotation) return;
    removeAnnotation(layer.id, annotation.id);
    requestAnimationFrame(() => closeButtonRef.current?.focus());
  }, [annotation, layer, removeAnnotation]);
  useEffect(() => {
    if (!annotation || !layer || !canAuthor) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      )
        return;
      event.preventDefault();
      removeSelectedAnnotation();
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [annotation, canAuthor, layer, removeSelectedAnnotation]);
  if (!layer) return null;
  const layerLegendEligibility = annotationLayerLegendEligibility(layer);
  const annotationSymbol = annotation ? annotationLegendSymbol(annotation) : null;
  const individualLegendDisabled = Boolean(layer.includeInLegend);
  const change = (update: (item: Annotation) => Annotation) =>
    annotation && updateAnnotation(layer.id, annotation.id, update);
  const changeAnnotationLegendLabel = (legendLabel: string) =>
    change((item) => {
      const { legendLabel: _current, ...rest } = item;
      void _current;
      return legendLabel.trim() ? ({ ...rest, legendLabel } as Annotation) : (rest as Annotation);
    });
  return (
    <aside
      className="inspector annotation-inspector"
      aria-label={`Annotation Inspector for ${annotation?.name ?? layer.name}`}
    >
      <div className="inspector-heading">
        <div>
          {annotation ? (
            <button
              aria-label={`Back to ${layer.name}`}
              className="annotation-back"
              onClick={() => {
                selectLayer(layer.id);
                requestAnimationFrame(() => closeButtonRef.current?.focus());
              }}
              title={`Back to ${layer.name}`}
              type="button"
            >
              <Icon name="collapse" />
              <span>{layer.name}</span>
            </button>
          ) : null}
          <h2>{annotation?.name ?? layer.name}</h2>
        </div>
        <button
          aria-label="Close Inspector"
          className="icon-button"
          onClick={() => {
            const layerControl = document.getElementById(`annotation-layer-select-${layer.id}`);
            closeInspector();
            requestAnimationFrame(() => layerControl?.focus());
          }}
          ref={closeButtonRef}
          type="button"
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="inspector-panel">
        <div className="inspector-content">
          {annotation ? (
            <>
              <section className="inspector-section">
                <label className="annotation-field">
                  <span>Name</span>
                  <input
                    maxLength={120}
                    onChange={(event) =>
                      change((item) => ({ ...item, name: event.target.value || item.name }))
                    }
                    value={annotation.name}
                  />
                </label>
                <div className="annotation-field">
                  <span>Annotation layer</span>
                  <button
                    aria-label="Annotation layer"
                    aria-expanded={layerMenuOpen}
                    aria-haspopup="menu"
                    className="annotation-layer-menu-trigger"
                    disabled={!canAuthor}
                    onClick={() => setLayerMenuOpen((open) => !open)}
                    ref={layerMenuButtonRef}
                    type="button"
                  >
                    <span className="annotation-layer-menu-label">
                      <Icon name="layers" />
                      <span>{layer.name}</span>
                    </span>
                    <Icon name="chevron-down" />
                  </button>
                  <AnchoredMenu
                    anchorRef={layerMenuButtonRef}
                    className="annotation-layer-menu"
                    label="Move annotation to layer"
                    onDismiss={() => setLayerMenuOpen(false)}
                    open={layerMenuOpen}
                  >
                    {project.annotationLayers.map((candidate) => (
                      <button
                        aria-current={candidate.id === layer.id ? 'true' : undefined}
                        key={candidate.id}
                        onClick={() => {
                          setLayerMenuOpen(false);
                          if (candidate.id === layer.id) return;
                          const result = moveAnnotationToLayer(
                            layer.id,
                            annotation.id,
                            candidate.id,
                          );
                          setNotice(result.ok ? null : result.message);
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <span className="annotation-layer-menu-label">
                          <Icon name="layers" />
                          <span>{candidate.name}</span>
                        </span>
                        {candidate.id === layer.id ? <Icon name="check" /> : null}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setLayerMenuOpen(false);
                        const result = moveAnnotationToNewLayer(layer.id, annotation.id);
                        setNotice(result.ok ? null : result.message);
                      }}
                      role="menuitem"
                      type="button"
                    >
                      <span className="annotation-layer-menu-label">
                        <Icon name="plus" />
                        <span>New annotation layer...</span>
                      </span>
                    </button>
                  </AnchoredMenu>
                </div>
                <label className="annotation-check">
                  <input
                    checked={annotation.visible}
                    onChange={(event) =>
                      change((item) => ({ ...item, visible: event.target.checked }))
                    }
                    type="checkbox"
                  />
                  <span>Visible</span>
                </label>
              </section>
              <section className="inspector-section">
                <h3>Legend</h3>
                <label className="annotation-check">
                  <input
                    aria-describedby={
                      !annotationSymbol || individualLegendDisabled
                        ? `annotation-legend-help-${annotation.id}`
                        : undefined
                    }
                    checked={annotation.includeInLegend === true}
                    disabled={!canAuthor || !annotationSymbol || individualLegendDisabled}
                    onChange={(event) =>
                      change((item) => {
                        const { includeInLegend: _current, ...rest } = item;
                        void _current;
                        return event.target.checked
                          ? ({ ...rest, includeInLegend: true } as Annotation)
                          : (rest as Annotation);
                      })
                    }
                    type="checkbox"
                  />
                  <span>Include in legend</span>
                </label>
                {!annotationSymbol || individualLegendDisabled ? (
                  <small className="reserved-note" id={`annotation-legend-help-${annotation.id}`}>
                    {individualLegendDisabled
                      ? 'This annotation layer is represented by one legend item.'
                      : 'This annotation type does not have a legend symbol.'}
                  </small>
                ) : null}
                {annotation.includeInLegend ? (
                  <label className="annotation-field">
                    <span>Legend label</span>
                    <input
                      aria-label="Legend label"
                      disabled={!canAuthor || individualLegendDisabled}
                      maxLength={ANNOTATION_LIMITS.nameLength}
                      onChange={(event) => changeAnnotationLegendLabel(event.target.value)}
                      placeholder={annotation.name}
                      value={annotation.legendLabel ?? ''}
                    />
                  </label>
                ) : null}
              </section>
              <section className="inspector-section">
                <div className="inspector-section-heading">
                  <h3>Geometry</h3>
                  <ContextHelp
                    label="Geometry"
                    text="Coordinates are longitude, then latitude, in degrees (WGS 84 / OGC:CRS84). You can also drag the annotation on the map."
                  />
                </div>
                <GeometryControls annotation={annotation} change={change} />
                {annotation.type === 'marker' ||
                annotation.type === 'text' ||
                annotation.type === 'line' ||
                annotation.type === 'callout' ? (
                  <NumberField
                    label={
                      annotation.type === 'callout'
                        ? 'Label height above map (m)'
                        : 'Height above map (m)'
                    }
                    min={0}
                    max={ANNOTATION_LIMITS.heightAboveMapM.maximum}
                    step="any"
                    value={annotationHeight(annotation)}
                    onChange={(height) =>
                      change((item) =>
                        item.type === 'callout'
                          ? { ...item, labelHeightAboveMapM: height }
                          : item.type === 'marker' || item.type === 'text' || item.type === 'line'
                            ? { ...item, heightAboveMapM: height }
                            : item,
                      )
                    }
                  />
                ) : null}
              </section>
              <section className="inspector-section">
                <h3>{annotation.type === 'focus-area' ? 'Focus area' : 'Appearance'}</h3>
                <ObjectControls annotation={annotation} change={change} />
                {annotation.type === 'focus-area' ? (
                  <label className="annotation-check">
                    <input
                      checked={project.activeFocusAreaId === annotation.id}
                      onChange={(event) => {
                        if (!event.target.checked) {
                          setActiveFocusArea(null);
                          return;
                        }
                        const current = project.annotationLayers
                          .flatMap((candidate) => candidate.annotations)
                          .find((candidate) => candidate.id === project.activeFocusAreaId);
                        if (current && current.id !== annotation.id) {
                          setFocusDialog({
                            kind: 'switch',
                            nextId: annotation.id,
                            currentName: current.name,
                          });
                        } else setActiveFocusArea(annotation.id);
                      }}
                      type="checkbox"
                    />
                    <span>Focus effect active</span>
                  </label>
                ) : null}
              </section>
              <button
                className="annotation-delete"
                disabled={!canAuthor}
                onClick={removeSelectedAnnotation}
                type="button"
              >
                <Icon name="trash" /> Delete annotation
              </button>
            </>
          ) : (
            <>
              <section className="inspector-section">
                <div className="read-only-setting annotation-layer-summary">
                  <span>Annotations</span>
                  <strong>{layer.annotations.length.toLocaleString()}</strong>
                </div>
                <label className="annotation-check">
                  <input
                    checked={layer.visible}
                    onChange={() => toggleLayerVisibility(layer.id)}
                    type="checkbox"
                  />
                  <span>Layer visible</span>
                </label>
              </section>
              <section className="inspector-section">
                <h3>Legend</h3>
                <label className="annotation-check">
                  <input
                    aria-describedby={
                      layerLegendEligibility.eligible
                        ? undefined
                        : `annotation-layer-legend-help-${layer.id}`
                    }
                    checked={layer.includeInLegend === true}
                    disabled={!canAuthor || !layerLegendEligibility.eligible}
                    onChange={(event) => {
                      const result = setAnnotationLayerLegend(
                        layer.id,
                        event.target.checked,
                        layer.legendLabel,
                      );
                      setNotice(result.ok ? null : result.message);
                    }}
                    type="checkbox"
                  />
                  <span>Include layer in legend</span>
                </label>
                {!layerLegendEligibility.eligible ? (
                  <small className="reserved-note" id={`annotation-layer-legend-help-${layer.id}`}>
                    {annotationLayerLegendEligibilityMessage(layerLegendEligibility)}
                  </small>
                ) : null}
                {layer.includeInLegend ? (
                  <label className="annotation-field">
                    <span>Legend label</span>
                    <input
                      aria-label="Layer legend label"
                      disabled={!canAuthor}
                      maxLength={ANNOTATION_LIMITS.nameLength}
                      onChange={(event) => {
                        const result = setAnnotationLayerLegend(layer.id, true, event.target.value);
                        setNotice(result.ok ? null : result.message);
                      }}
                      placeholder={layer.name}
                      value={layer.legendLabel ?? ''}
                    />
                  </label>
                ) : null}
              </section>
              <section className="inspector-section">
                <h3>Add annotation</h3>
                {canAuthor ? (
                  <>
                    <div className="annotation-tool-grid">
                      {TOOLS.map((tool) => (
                        <button
                          aria-pressed={shell.annotationCreationTool === tool.id}
                          className={
                            shell.annotationCreationTool === tool.id
                              ? 'annotation-tool-active'
                              : undefined
                          }
                          key={tool.id}
                          onClick={() => {
                            if (shell.annotationCreationTool === tool.id) {
                              cancelDrawing();
                              return;
                            }
                            const existingFocus =
                              tool.id === 'focus-area'
                                ? layer.annotations.find(
                                    (candidate) => candidate.type === 'focus-area',
                                  )
                                : undefined;
                            if (existingFocus) {
                              setFocusDialog({ kind: 'replace', existingId: existingFocus.id });
                              return;
                            }
                            startDrawing(tool.id);
                          }}
                          type="button"
                        >
                          <Icon name={tool.icon} />
                          <span>{tool.label}</span>
                        </button>
                      ))}
                    </div>
                    {shell.annotationCreationTool ? (
                      <div className="annotation-drawing-status" role="status">
                        <strong>
                          {TOOLS.find((tool) => tool.id === shell.annotationCreationTool)?.label}{' '}
                          active
                        </strong>
                        <span>
                          {shell.annotationCreationTool === 'marker' ||
                          shell.annotationCreationTool === 'text'
                            ? 'Click the map to place it.'
                            : shell.annotationCreationTool === 'rectangle' ||
                                shell.annotationCreationTool === 'ellipse' ||
                                shell.annotationCreationTool === 'callout'
                              ? `${shell.annotationDraftCoordinates.length ? 'Click the second position.' : 'Click the first position.'}`
                              : 'Click vertices; double-click or press Enter to finish.'}
                        </span>
                        <button onClick={cancelDrawing} type="button">
                          Cancel (Esc)
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="reserved-note">
                    <Icon name="lock" /> Annotation authoring requires Pro. Existing annotations
                    remain visible and preserved.
                  </p>
                )}
              </section>
            </>
          )}
          {notice ? (
            <p className="inspector-notice" role="status">
              {notice}
            </p>
          ) : null}
        </div>
      </div>
      {focusDialog?.kind === 'replace' ? (
        <ConfirmationDialog
          confirmLabel="Replace focus area"
          description="This annotation layer already has a focus area. An annotation layer can currently contain one focus area. Replace the existing focus area?"
          onCancel={() => setFocusDialog(null)}
          onConfirm={() => {
            const existingId = focusDialog.existingId;
            setFocusDialog(null);
            startDrawing('focus-area', existingId);
          }}
          title="Replace focus area?"
        />
      ) : null}
      {focusDialog?.kind === 'switch' && annotation?.type === 'focus-area' ? (
        <ConfirmationDialog
          confirmLabel="Switch focus area"
          description={`Only one focus effect can currently be active at a time. Switch from “${focusDialog.currentName}” to “${annotation.name}”?`}
          onCancel={() => setFocusDialog(null)}
          onConfirm={() => {
            const nextId = focusDialog.nextId;
            setFocusDialog(null);
            setActiveFocusArea(nextId);
          }}
          title="Switch active focus area?"
        />
      ) : null}
    </aside>
  );
}
